import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import SearchInput from "components/inputs/SearchInput/SearchInput";
import { ActiveLabel } from "components/table/components/ActiveLabel/ActiveLabel";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import { OutNotificationItem } from "store/outAppNotifications/slice";

import styles from "./SegmentNotificationsTab.module.scss";

type Props = {
  selectedNotification?: OutNotificationItem;
  onSelectionChange: (notification?: OutNotificationItem) => void;
  onLoadingChange: (isLoading: boolean) => void;
};

const isNotificationEligible = (notification: OutNotificationItem) =>
  notification.type === "manual" && Boolean(notification.isActive);

export const SegmentNotificationsTab: React.FC<Props> = ({
  selectedNotification,
  onSelectionChange,
  onLoadingChange,
}) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<OutNotificationItem[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    onLoadingChange(isLoading);
  }, [isLoading, onLoadingChange]);

  useEffect(() => {
    let isMounted = true;

    const loadNotifications = async () => {
      setIsLoading(true);

      try {
        const result = await instance.post(apiRoutes.notificationsList, {
          limit: 1000,
          offset: 0,
          type: "manual",
          title: "",
          isActive: 1,
          order: {
            title: "asc",
            isActive: "desc",
          },
        });

        if (isMounted) {
          const eligibleNotifications = (result.data.items ?? []).filter(
            isNotificationEligible,
          );

          setNotifications(eligibleNotifications);

          if (
            selectedNotification &&
            !eligibleNotifications.some(
              (item: OutNotificationItem) =>
                item.id === selectedNotification.id,
            )
          ) {
            onSelectionChange(undefined);
          }
        }
      } catch (error) {
        console.error("Unable to load notifications", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadNotifications();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredNotifications = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    if (!query) return notifications;

    return notifications.filter((notification) =>
      [notification.id, notification.title, notification.type].some((value) =>
        String(value ?? "")
          .toLowerCase()
          .includes(query),
      ),
    );
  }, [notifications, searchValue]);

  if (isLoading) return <ModalTabSkeleton />;

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        <SearchInput
          placeholder={t("searchNotification")}
          value={searchValue}
          onChange={setSearchValue}
          isSmall
        />
      </div>

      <div className={styles.table}>
        <div className={`${styles.row} ${styles.header}`}>
          <div className={styles.radioCell} aria-hidden="true" />
          <div className={styles.id}>ID</div>
          <div className={styles.name}>{t("name").toUpperCase()}</div>
          <div className={styles.type}>{t("type").toUpperCase()}</div>
          <div className={styles.active}>{t("active").toUpperCase()}</div>
        </div>

        {filteredNotifications.length === 0 ? (
          <div className={styles.empty}>{t("noNotificationResultsFound")}</div>
        ) : (
          filteredNotifications.map((notification) => {
            const isSelected = selectedNotification?.id === notification.id;

            return (
              <div
                className={`${styles.row} ${isSelected ? styles.selected : ""}`}
                key={notification.id}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => onSelectionChange(notification)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelectionChange(notification);
                  }
                }}
              >
                <div className={styles.radioCell}>
                  <span
                    className={`${styles.radio} ${isSelected ? styles.radioChecked : ""}`}
                  />
                </div>
                <div className={styles.id}>{notification.id}</div>
                <div className={styles.name}>{notification.title}</div>
                <div className={styles.type}>{t(notification.type)}</div>
                <div className={styles.active}>
                  <ActiveLabel isActive={Boolean(notification.isActive)} />
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
